import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";

import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";

import { CopyIcon } from "@/assets";
import { InputOTP, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuthState } from "@/context";
import { ToastMessage, type } from "@/lib/toastr";
import { useCopy } from "@/lib/useCopyToClipBoard";
import {
  getAccountName,
  getAllBank,
  getCryptoPayInAddress,
  getFiatPayIn,
} from "@/services/Home.service";
import { createTransaction } from "@/services/Transactions.service";
import { getUserByName } from "@/services/Users.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import IMG from "../../../../assets/payin.png";
import Img2 from "../../../../assets/qr.png";
import DebounceSearch from "../../../../components/DebouneSearch";
import Selector from "../../../../components/Selector";
import { CustomDatePicker } from "@/components/custom-date-picker";
import { formatType } from "@/lib/helper";

const AddTransaction = () => {
  const q = useQueryClient();
  const { useCurrencies } = useAuthState();
  const { copyToClipboard } = useCopy();

  const currenciesList = useCurrencies();
  const currenciesFormatted = currenciesList?.data?.data?.data?.map((i) => {
    return {
      label: i?.code,
      value: i?.code,
    };
  });
  const currenciesFormattedCrypto = currenciesList?.data?.data?.data?.filter(
    (i) => i?.currency?.type === "crypto"
  );
  const currenciesFormattedFiat = currenciesList?.data?.data?.data?.filter(
    (i) => i?.currency?.type !== "crypto"
  );

  const [step, setStep] = useState(1);
  const [prev, setPrev] = useState(Number);
  const [selectedWho, setSelectedWho] = useState("business");
  const [selected, setSelected] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState({});
  const [selectedCurrencyTo, setSelectedCurrencyTo] = useState({});
  const [selectedType, setSelectedType] = useState({});
  const [selectedValue, setSelectedValue] = useState("");
  const [selectedAmount, setSelectedAmount] = useState(Number);
  const [selectedFee, setSelectedFee] = useState(Number);
  const [selectedRate, setSelectedRate] = useState(Number);
  const [selectedNetwork, setSelectedNetwork] = useState({});
  const [selectedBank, setSelectedBank] = useState({});
  const [selectedUpdate, setSelectedUpdate] = useState(false);
  const [selectedAddDate, setSelectedAddDate] = useState(false);
  const [addDate, setAddDate] = useState("");
  const [dpReference, setDpReference] = useState("");
  const [reference, setReference] = useState("");
  const [account, setAccount] = useState("");
  const [accountName, setAccountName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [twoFa, setTwoFA] = useState("");
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const checkSearch = search === "" ? false : true;

  const { data } = useQuery({
    queryKey: ["search_user", search],
    queryFn: () =>
      getUserByName(search).then((res) =>
        res?.data?.data?.data.map((user) => ({
          label: `${user?.firstName} ${user?.lastName}`,
          value: user?.id,
        }))
      ),
    enabled: !!checkSearch,
  });

  const getCurrency = currenciesList?.data?.data?.data
    ?.filter(
      (i) =>
        i.code === selectedCurrency?.label?.toLocaleUpperCase() && i?.currency
    )
    ?.flat();
  const getCurrencyTo = currenciesList?.data?.data?.data
    ?.filter(
      (i) =>
        i.code === selectedCurrencyTo?.label?.toLocaleUpperCase() && i?.currency
    )
    ?.flat();

  const fiatCheck =
    getCurrency === undefined
      ? false
      : getCurrency[0]?.currency?.type === "fiat"
        ? true
        : false;
  const fiat = useQuery({
    queryKey: ["fiat"],
    queryFn: async () => await getFiatPayIn(selectedCurrency?.label),
    refetchOnMount: "always",
    enabled: !!fiatCheck,
  });

  const cryptoCheck =
    getCurrency === undefined
      ? false
      : getCurrency[0]?.currency?.type === "crypto"
        ? true
        : false;
  const cryptoCheck2 = selectedNetwork?.label !== undefined ? true : false;

  const walletAddress = useQuery({
    queryKey: ["walletAddy", selectedNetwork?.label],
    queryFn: async () =>
      await getCryptoPayInAddress(
        selectedCurrency?.label,
        selectedNetwork?.value
      ),
    refetchOnMount: "always",
    enabled: !!cryptoCheck && !!cryptoCheck2,
  });

  const bankRes = useQuery({
    queryKey: ["bank"],
    queryFn: async () => await getAllBank(),
    refetchOnMount: "always",
  });

  const allBanks = new Set();
  const filteredArr = bankRes?.data?.data?.data?.filter((el) => {
    const duplicate = allBanks.has(el.code.toLowerCase());
    allBanks.add(el.code);
    return !duplicate;
  });

  useEffect(() => {
    if (account?.length === 10) {
      setIsLoading(true);
      getAccountName({
        bank: selectedBank?.value,
        account,
      })
        .then((res) => {
          setIsLoading(false);
          setAccountName(res?.data?.data?.account_name);
        })
        .catch((err) => {
          setIsLoading(false);
          setAccountName("");
          ToastMessage(
            type.ERROR,
            err.response.data.message === "Unable to verify account details"
              ? "Check your account details and try again!"
              : err.response.data.message
          );
        });
    }
  }, [account]);

  const handleClose = () => {
    setSelectedWho("business");
    setValue("");
    setLabel("");
    setSelectedValue("");
    setAccount("");
    setAccountName("");
    setReference("");
    setSelectedBank({});
    setSelectedNetwork({});
    setSelectedCurrency({});
    setSelectedCurrencyTo({});
    setAddDate("");
    setSelectedType({});
    setSelectedUpdate(false);
    setSelectedAmount("");
    setSelectedFee("");
    setSelectedRate("");
    setStep(1);
  };

  const mutation = useMutation({
    mutationFn: (data) => createTransaction(data),
    mutationKey: ["createTransaction"],

    onSuccess: (res) => {
      setOpen(false);
      q.resetQueries();

      setStep(1);

      ToastMessage(type.SUCCESS, "Transaction record created successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  const handleSubmit = async () => {
    const formData = {
      twoFactorAuthenticationCode: twoFa,
      date: addDate,
      type:
        selectedWho === "business"
          ? selectedType?.value?.replace(" ", "")
          : "cross",
      entity: selectedWho,
      customerId: selectedValue,
      currency: selectedCurrency?.label,
      quoteCurrency:
        selectedWho === "customer"
          ? selectedCurrencyTo?.label
          : selectedCurrency?.label,
      depositReference: dpReference ? dpReference : "string",
      withdrawalReference: reference ? reference : "string",
      fee: Number(selectedFee),
      amount: Number(selectedAmount),
      rate: Number(selectedRate),
      beneficiary: {
        bank: {
          accountNumber:
            selectedWho === "customer"
              ? account
              : getCurrency[0]?.currency?.type === "fiat"
                ? account
                : "string",
          bankCode:
            selectedWho === "customer"
              ? selectedBank?.value
              : getCurrency[0]?.currency?.type === "fiat"
                ? selectedBank?.value
                : "string",
        },
        crypto: {
          network:
            getCurrency[0]?.currency?.type !== "fiat"
              ? selectedNetwork?.value
              : "bep20",
          address:
            getCurrency[0]?.currency?.type !== "fiat"
              ? accountName === ""
                ? "string"
                : accountName
              : "string",
        },
      },
      updateBalance: selectedUpdate,
    };

    await mutation.mutate(formData);
  };

  return (
    <CustomModal
      button={
        <Button
          className="bg-gradient-to-r from-primary to-primary-2 ring-transparent focus-visible:ring-transparent"
          onClick={() => setOpen(true)}
        >
          Add Transaction
        </Button>
      }
      size="md"
      open={open}
      onOpenChange={(e) => {
        if (!e) handleClose();
      }}
    >
      <div className="flex flex-col gap-8">
        <DialogHeader className="bg-off-white-1 py-6 px-8 rounded-t-[16px]">
          <DialogTitle className="text-sm font-normal capitalize text-primary-3">
            {step === 1 || step === 2
              ? "Add transaction"
              : step === 4
                ? "Enter 2FA"
                : "Review transaction"}
          </DialogTitle>
          {step !== 3 && (
            <DialogDescription className="text-sm">
              {step === 4
                ? "Enter 2fa to authorize this transaction"
                : "Fill in the details below to add a transaction"}
            </DialogDescription>
          )}
        </DialogHeader>

        <div
          className={cn(
            "flex flex-col gap-7 px-8 pb-8",
            step === 2 || step === 4 ? "h-fit" : "h-[350px] overflow-scroll"
          )}
        >
          {step === 1 ? (
            <>
              <div className="flex flex-col gap-5">
                <Label className="text-gray-80 text-base">
                  Who is this transaction for?
                </Label>

                <RadioGroup
                  onValueChange={(e) => {
                    setSelectedWho(e);
                    setValue("");
                    setLabel("");
                    setSelectedValue("");
                    setAccount("");
                    setAccountName("");
                    setReference("");
                    setAddDate("");
                    setSelectedBank({});
                    setSelectedNetwork({});
                    setSelectedCurrency({});
                    setSelectedType({});
                    setSelectedUpdate(false);
                    setSelectedAmount("");
                    setSelectedFee("");
                    setSelectedRate("");
                    setSelectedCurrencyTo({});
                  }}
                  defaultValue={selectedWho}
                  className="flex items-center gap-6"
                >
                  <div className="flex items-center space-x-2 space-y-0">
                    <RadioGroupItem value="business" />

                    <Label className="font-medium text-[#34373F]">
                      For business
                    </Label>
                  </div>

                  <div className="flex items-center space-x-3 space-y-0">
                    <RadioGroupItem value="customer" />

                    <Label className="font-medium text-[#34373F]">
                      For customer
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <Separator />

              <div className="w-full flex flex-col gap-5">
                {selectedWho !== "business" && (
                  <div className="flex flex-col w-full items-start gap-1 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
                    <Label className="text-xs text-gray-70">
                      Select customer
                    </Label>
                    <DebounceSearch
                      items={data || []}
                      value={label}
                      onSelect={(value, label) => {
                        setValue(value || "");
                        setLabel(label || "");
                        setSelectedValue(value);
                      }}
                      showInput
                      onSearchChange={setSearch}
                      searchPlaceholder={`Search customer
                    ...`}
                      noResultsMsg={`No customer found`}
                      selectItemMsg={`Select customer`}
                      setSelectedValue={setSelectedValue}
                    />
                  </div>
                )}
                {selectedWho === "business" && (
                  <div className="flex flex-col w-full items-start gap-1 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
                    <Label className="text-xs text-gray-70">
                      Transaction Type
                    </Label>

                    <Selector
                      options={[
                        { label: "Pay out", value: "pay out" },
                        { label: "Pay in", value: "pay in" },
                      ]}
                      selected={selectedType}
                      setSelected={setSelectedType}
                    />
                  </div>
                )}
                <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
                  <Label className="text-xs text-gray-70">
                    {selectedWho === "customer" ? "From Currency" : "Currency"}
                  </Label>

                  <Selector
                    options={
                      selectedWho === "customer"
                        ? currenciesFormattedCrypto?.map((i) => {
                          return { label: i?.code, value: i?.code };
                        })
                        : currenciesFormatted || []
                    }
                    selected={selectedCurrency}
                    setSelected={setSelectedCurrency}
                  />
                </div>
                {selectedWho === "customer" && (
                  <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
                    <Label className="text-xs text-gray-70">To Currency</Label>
                    <Selector
                      options={
                        selectedWho === "customer"
                          ? currenciesFormattedFiat?.map((i) => {
                            return { label: i?.code, value: i?.code };
                          })
                          : currenciesFormatted || []
                      }
                      selected={selectedCurrencyTo}
                      setSelected={setSelectedCurrencyTo}
                    />
                  </div>
                )}

                {selectedWho === "customer" && (
                  <div
                    className={cn(
                      "flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2",
                      selectedCurrency?.label !== undefined &&
                        selectedAmount !== ""
                        ? "mt-4"
                        : "mt-0"
                    )}
                  >
                    <Label className="text-xs text-gray-70">Rate</Label>

                    <Input
                      className="p-0 border-none bg-transparent rounded-none h-6 w-full pb-2"
                      onChange={(e) => setSelectedRate(e?.target?.value)}
                      placeholder="Enter rate"
                      value={selectedRate === 0 ? "" : selectedRate}
                      type="number"
                    />
                  </div>
                )}
                <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 ">
                  <Label className="text-xs text-gray-70">Fee</Label>
                  <Input
                    className="p-0 border-none bg-transparent rounded-none h-6 w-full pb-2"
                    value={selectedFee === 0 ? "" : selectedFee}
                    onChange={(e) => setSelectedFee(e?.target?.value)}
                    placeholder="Enter fee"
                    type="number"
                  />
                </div>
                <div className="grid">
                  <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2">
                    <Label className="text-xs text-gray-70">Amount</Label>

                    <div className="flex w-full items-center justify-between gap-3">
                      <Input
                        className="p-0 border-none bg-transparent rounded-none h-6 w-full pb-2"
                        onChange={(e) => setSelectedAmount(e?.target?.value)}
                        placeholder="Enter Amount"
                        value={selectedAmount === 0 ? "" : selectedAmount}
                        type='number'
                      />

                      {selectedCurrency && (
                        <span className="text-xs text-[#525866] w-[45%] text-right">
                          {selectedCurrency?.value?.toLocaleUpperCase()}{" "}
                          {selectedFee} Fee
                        </span>
                      )}
                    </div>
                  </div>

                  {selectedCurrency && selectedAmount > 0 && (
                    <Label className="text-sm text-[#1A1100]">
                      You’ll {selectedType?.label ==='Pay out'? 'be debited':'receive'}{" "}
                      {/*   /${new Intl.NumberFormat().format(   (selectedAmount - selectedFee) * selectedRate
                      // ,{
                        
                       })} 
                        ${new Intl.NumberFormat().format(
                              selectedAmount - selectedFee
                            )}
                        */}
                      {selectedWho === "customer" ? (
                        <>
                          {`${getCurrencyTo[0]?.currency?.type === "fiat"
                            ? getCurrencyTo[0]?.currency?.symbol
                            : ""
                            } 
                          ${((selectedAmount - selectedFee) * selectedRate)?.toLocaleString(
                              undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }
                            )}
                          ${getCurrencyTo[0]?.currency?.type === "crypto"
                              ? getCurrencyTo[0]?.currency?.code
                              : ""
                            }`}
                        </>
                      ) : (
                        <>
                          {`${getCurrency[0]?.currency?.type === "fiat"
                            ? getCurrency[0]?.currency?.symbol
                            : ""
                            } 
                               ${(selectedAmount - selectedFee)?.toLocaleString(
                              undefined, {
                              minimumFractionDigits: getCurrency[0]?.currency?.code === 'BTC' ? 4 : 2,
                              maximumFractionDigits: getCurrency[0]?.currency?.code === 'BTC' ? 4 : 2
                            }
                            )}
                               ${getCurrency[0]?.currency?.type !== "fiat"
                              ? getCurrency[0]?.currency?.code
                              : ""
                            }`}
                        </>
                      )}
                    </Label>
                  )}
                </div>

                {(selectedType?.value === "pay in" ||
                  selectedWho === "customer") && (
                    <div className="flex flex-col w-full items-start gap-1.5 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 ">
                      <Label className="text-xs text-[#696E7C]">
                        Deposit reference
                      </Label>
                      <div className="flex w-full items-center">
                        <Input
                          className="p-0 border-none h-4 bg-transparent"
                          value={dpReference}
                          onChange={(e) => setDpReference(e?.target?.value)}
                        />
                      </div>
                    </div>
                  )}
                {(selectedType?.value === "pay out" ||
                  selectedWho === "customer") && (
                    <div className="flex flex-col w-full items-start gap-1.5 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 ">
                      <Label className="text-xs text-[#696E7C]">
                        Withdrawal reference
                      </Label>
                      <div className="flex w-full items-center">
                        <Input
                          className="p-0 border-none h-4 bg-transparent"
                          value={reference}
                          onChange={(e) => setReference(e?.target?.value)}
                        />
                      </div>
                    </div>
                  )}

                <div className="flex flex-row items-start space-x-3 space-y-0 ">
                  <Checkbox
                    defaultChecked={selectedAddDate}
                    onCheckedChange={setSelectedAddDate}
                  />

                  <div className="space-y-1 leading-none">
                    <Label className="text-xs text-gray-70">
                      {selectedAddDate ? "Remove" : "Add"} date
                    </Label>
                  </div>
                </div>
                {selectedAddDate && (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="date" className="text-xs">
                      Date
                    </Label>
                    <CustomDatePicker
                      date={addDate}
                      setDate={(e) => {
                        setAddDate(formatType(e));
                      }}
                    />
                  </div>
                )}
                <div className="flex flex-row items-start space-x-3 space-y-0 ">
                  <Checkbox
                    defaultChecked={selectedUpdate}
                    onCheckedChange={setSelectedUpdate}
                  />

                  <div className="space-y-1 leading-none">
                    <Label className="text-xs text-gray-70">
                      Update Balance
                    </Label>
                  </div>
                </div>
              </div>
            </>
          ) : step === 2 ? (
            <div className="grid gap-4">
              <div className="flex flex-col w-full items-start gap-1.5 border-[1px]  rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 ">
                <Label className="text-xs text-[#696E7C]">
                  {(selectedWho !== "customer" || selectedWho === "business") &&
                    getCurrency[0]?.currency?.type === "crypto"
                    ? " Wallet address"
                    : "Bank name"}
                </Label>

                {((selectedWho === "customer" || selectedWho === "business") &&
                  getCurrency[0]?.currency?.type === "fiat") ||
                  getCurrencyTo[0]?.currency?.type === "fiat" ? (
                  <Selector
                    options={
                      filteredArr?.map((i: any) => {
                        return { label: i.name, value: i.code };
                      }) || []
                    }
                    selected={selectedBank}
                    setSelected={setSelectedBank}
                  />
                ) : (
                  <div className="flex w-full items-center">
                    <Input
                      className="p-0 border-none h-4 bg-transparent"
                      onChange={(e) => setAccountName(e?.target?.value)}
                      value={accountName}
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-col w-full items-start gap-1 border-[1px] rounded-[8px] px-2 py-2 h-[52px] border-input-border-2 justify-between">
                <Label className="text-xs text-gray-70">
                  {selectedWho === "customer" ||
                    (selectedWho === "business" &&
                      getCurrency[0]?.currency?.type === "fiat")
                    ? "Account number"
                    : "Network"}
                </Label>
                {selectedWho === "customer" ||
                  (selectedWho === "business" &&
                    getCurrency[0]?.currency?.type === "fiat") ? (
                  <div className="flex w-full items-center justify-between">
                    <div className=" max-w-sm  w-[40%]">
                      <Input
                        className="p-0 border-none h-4 bg-transparent "
                        onChange={(e) => setAccount(e?.target?.value)}
                        value={account}
                      />
                    </div>
                    <span className="text-primary text-xs ">
                      {isLoading ? (
                        <Loader2 className="animate-spin w-4 h-4" />
                      ) : (
                        <>
                          {accountName?.length > 20
                            ? accountName.slice(0, 18) + "..."
                            : accountName}
                        </>
                      )}
                    </span>
                  </div>
                ) : (
                  <Selector
                    options={[
                      { label: "BEP-20", value: "bep20" },
                      { label: "TRC-20", value: "trc20" },
                      { label: "ERC-20", value: "erc20" },
                    ]}
                    selected={selectedNetwork}
                    setSelected={setSelectedNetwork}
                  />
                )}
              </div>
            </div>
          ) : step === 3 ? (
            <div className="grid gap-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#546974]">Currency</p>
                <p className="text-[#2A0028] text-sm text-right">
                  {selectedCurrency?.label}
                </p>
              </div>
              <Separator />
              {selectedWho === "customer" && (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#546974]">To Currency</p>
                    <p className="text-[#2A0028] text-sm text-right">
                      {selectedCurrencyTo?.label}
                    </p>
                  </div>
                  <Separator />
                </>
              )}
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#546974]">
                  {selectedType?.value === "pay in"
                    ? "Payout "
                    : selectedType?.value === "pay out"
                      ? "Payout "
                      : ""}
                  Amount
                </p>
                <p className="text-[#2A0028] text-sm text-right">
                  {selectedType?.value === "pay in" ||
                    selectedType?.value === "pay out" ? (
                    <>
                      {getCurrency[0]?.currency?.type === "fiat"
                        ? `${selectedCurrency?.label
                        } ${selectedAmount?.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                        : `${selectedAmount?.toLocaleString("en-US", {
                          minimumFractionDigits: selectedCurrency?.label === 'BTC' ? 4 : 2,
                          maximumFractionDigits: selectedCurrency?.label === 'BTC' ? 4 : 2
                        })

                        } ${selectedCurrency?.label}`}
                    </>
                  ) : (
                    `${selectedAmount?.toLocaleString("en-US", {
                      minimumFractionDigits: selectedCurrency?.label === 'BTC' ? 4 : 2,
                      maximumFractionDigits: selectedCurrency?.label === 'BTC' ? 4 : 2
                    })

                    } ${selectedCurrency?.label}`
                  )}
                </p>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#546974]">Fee</p>
                <p className="text-[#2A0028] text-sm text-right">
                  {getCurrency[0]?.currency?.type === "fiat"
                    ? `${selectedCurrency?.label} ${selectedFee}`
                    : `${selectedFee} ${selectedCurrency?.label}`}
                </p>
              </div>
              <Separator />
              {selectedWho === "customer" && (
                <>
                  <div className="flex items-center justify-between">
                    {" "}
                    <p className="text-sm text-[#546974]">Rate</p>
                    <p className="text-[#2A0028] text-sm text-right">
                      1 {selectedCurrency?.value} / {selectedCurrencyTo?.label}{" "}
                      {selectedRate}
                    </p>
                  </div>
                  <Separator />
                </>
              )}
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#546974]">Amount</p>
                {/* <p className="text-sm text-[#546974]">Amount to Receive</p> */}
                <p className="text-[#2A0028] text-sm text-right">
                  {selectedType?.value === "pay in" ||
                    selectedType?.value === "pay out" ? (
                    <>
                      {getCurrency[0]?.currency?.type === "fiat"
                        ? `${selectedCurrency?.label} ${(
                          selectedAmount - selectedFee
                        )?.toLocaleString("en-US",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2


                          })
                        }`
                        : `
                          ${(selectedAmount - selectedFee)?.toLocaleString("en-US", {
                          minimumFractionDigits: selectedCurrency?.label === 'BTC' ? 4 : 2,
                          maximumFractionDigits: selectedCurrency?.label === 'BTC' ? 4 : 2
                        })
                        } 
                         ${selectedCurrency?.label}`}
                    </>
                  ) : (
                    `${selectedCurrencyTo?.label} ${(
                      (selectedAmount - selectedFee) *
                      selectedRate
                    )?.toLocaleString("en-US"
                      ,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      }


                    )}`
                  )}
                </p>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                {(selectedWho === "customer" &&
                  getCurrencyTo[0]?.currency?.type !== "fiat") ||
                  (selectedWho === "business" &&
                    getCurrency[0]?.currency?.type !== "fiat") ? (
                  <>
                    <p className="text-sm text-[#546974]">Network</p>
                    <p className="text-[#2A0028] text-sm text-right">ERC-20</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-[#546974]">To</p>
                    <p className="text-[#2A0028] text-sm text-right">
                      {accountName}
                    </p>
                  </>
                )}
              </div>
              <Separator />

              <div className="flex items-center justify-between">
                <p className="text-sm text-[#546974]">
                  {(selectedWho === "customer" &&
                    getCurrencyTo[0]?.currency?.type !== "fiat") ||
                    (selectedWho === "business" &&
                      getCurrency[0]?.currency?.type !== "fiat")
                    ? "To"
                    : "Bank Name"}
                </p>
                <p className="text-[#2A0028] text-sm text-right">
                  {(selectedWho === "customer" &&
                    getCurrencyTo[0]?.currency?.type !== "fiat") ||
                    (selectedWho === "business" &&
                      getCurrency[0]?.currency?.type !== "fiat")
                    ? selectedValue
                    : selectedBank?.label}
                </p>
              </div>
              <Separator />

              <div className="flex items-center justify-between">
                <p className="text-sm text-[#546974]">
                  {(selectedWho === "customer" &&
                    getCurrencyTo[0]?.currency?.type !== "fiat") ||
                    (selectedWho === "business" &&
                      getCurrency[0]?.currency?.type !== "fiat")
                    ? "Wallet Address"
                    : "Account Number"}
                </p>
                <p className="text-[#2A0028] text-sm text-right">
                  {(selectedWho === "customer" &&
                    getCurrencyTo[0]?.currency?.type !== "fiat") ||
                    (selectedWho === "business" &&
                      getCurrency[0]?.currency?.type !== "fiat")
                    ? accountName?.length > 10
                      ? accountName?.slice(0, 15) + "..."
                      : accountName
                    : account}
                </p>
              </div>
              <Separator />
              {selectedWho === "customer" ? (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#546974]">Deposit reference</p>
                    <p className="text-[#2A0028] text-sm text-right">
                      {dpReference}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#546974]">
                      Withdrawal reference
                    </p>
                    <p className="text-[#2A0028] text-sm text-right">
                      {reference}
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#546974]">
                    {selectedType?.value === "pay out"
                      ? "Withdrawal reference"
                      : "Deposit reference"}
                  </p>
                  <p className="text-[#2A0028] text-sm text-right">
                    {selectedType?.value === "pay out"
                      ? reference
                      : dpReference}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div>
              <InputOTP
                maxLength={6}
                onChange={(e) => setTwoFA(e)}
                className="flex justify-between gap-8"
              >
                <InputOTPSlot index={0} className=" w-14 h-14" />
                <InputOTPSlot index={1} className=" w-14 h-14" />
                <InputOTPSlot index={2} className="w-14 h-14" />
                <InputOTPSlot index={3} className=" w-14 h-14" />
                <InputOTPSlot index={4} className=" w-14 h-14" />
                <InputOTPSlot index={5} className="w-14 h-14" />
              </InputOTP>
            </div>
          )}
        </div>
      </div>
      <DialogFooter className="px-6 py-4 flex flex-row justify-between items-center bg-off-white-1 rounded-b-[16px]">
        {step === 1 ? (
          <DialogClose asChild>
            <Button
              type="button"
              variant="ghost"
              className=" text-primary0 h-12 rounded-[10px] p-[8px 12px] hover:text-primary0"
              onClick={() => {
                setSelectedWho("business");
                setValue("");
                setLabel("");
                setSelectedValue("");
                setAccount("");
                setAccountName("");
                setReference("");
                setSelectedBank({});
                setSelectedNetwork({});
                setSelectedCurrency({});
                setSelectedCurrencyTo({});
                setSelectedType({});
                setSelectedUpdate(false);
                setSelectedAmount("");
                setSelectedFee("");
                setSelectedRate("");
                setStep(1);
                setOpen(false);
              }}
            >
              <ChevronLeft /> Cancel
            </Button>
          </DialogClose>
        ) : (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setStep(step - 1);
            }}
            className=" text-primary0 h-12 rounded-[10px] p-[8px 12px] hover:text-primary0"
          >
            <ChevronLeft /> Back
          </Button>
        )}

        {step === 1 ? (
          <Button
            onClick={() => setStep(step + 1)}
            className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]"
          >
            Next <ChevronRight />
          </Button>
        ) : step === 2 ? (
          <Button
            onClick={() => setStep(step + 1)}
            className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]"
          >
            Next <ChevronRight />
          </Button>
        ) : step === 3 ? (
          <Button
            onClick={() => setStep(step + 1)}
            className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]"
          >
            Add Transaction
            <ChevronRight />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]"
          >
            {mutation?.isPending && (
              <Loader2 className="text-white animate-spin" />
            )}
            Submit
            <ChevronRight />
          </Button>
        )}
      </DialogFooter>
    </CustomModal>
  );
};

export default AddTransaction;
